import React from "react";
import PropTypes from "prop-types";
import { Row, Col } from "reactstrap";
import NeoButton from "../../../../../components/Common/NeoButton";
import NeoBadge from "../../../../../components/Common/NeoBadge";
import NeoCard from "../../../../../components/Common/NeoCard";
import { formatIsoDatePtBr } from "../../../../../utils/tenantLifecycle";

const TRIAL_BILLING_STATUSES = new Set(["trial", "trial_started", "trial_active", "trialing"]);

const formatTrialRange = (tenant) => {
    const start = formatIsoDatePtBr(tenant.trialStartDate);
    const end = formatIsoDatePtBr(tenant.trialEndsAt);
    if (start && end) return `${start} a ${end}`;
    if (end) return `Até ${end}`;
    return null;
};

const DetailSubscription = ({ tenant, setTenant, handleLogAction, onAlterPlan, onAlterTrial }) => {
    // Build plan display from real tenant data
    const planName = tenant.platformPlanTitle || tenant.planId || "—";
    const planPrice = tenant.amount > 0
        ? `R$ ${Number(tenant.amount).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`
        : "Gratuito";

    // Extract effective features for display
    const ef = tenant.effectiveFeatures || {};
    const features = [];
    if (ef.maxMembers) features.push(`Até ${ef.maxMembers} alunos ativos`);
    if (ef.maxUsers) features.push(`Até ${ef.maxUsers} usuários simultâneos`);
    if (ef.hasFinanceiro) features.push("Módulo Financeiro completo");
    if (ef.hasCRM) features.push("CRM de Vendas integrado");
    if (ef.hasCompetitions) features.push("Módulo de Competições");
    if (ef.hasReports) features.push("Relatórios avançados");
    if (features.length === 0) features.push("Recursos padrão do plano");

    const maxMembers = ef.maxMembers ? `${ef.maxMembers} alunos` : "A consultar";
    const trialRange = formatTrialRange(tenant);
    const isTrial = TRIAL_BILLING_STATUSES.has(tenant.billingStatus);

    return (
        <NeoCard
            title="Gerenciamento de Assinatura e Planos"
            headerActions={
                <NeoBadge color={tenant.status === "active" ? "success" : tenant.status === "trial" ? "info" : tenant.status === "trial_scheduled" ? "warning" : tenant.status === "blocked" ? "danger" : "muted"}>
                    {tenant.status === "active"
                        ? "Assinatura Ativa"
                        : tenant.status === "trial"
                        ? "Em Período de Teste"
                        : tenant.status === "trial_scheduled"
                        ? "Trial Agendado"
                        : tenant.status === "blocked"
                        ? "Acesso Bloqueado"
                        : "Contrato Cancelado"}
                </NeoBadge>
            }
            thinBorder={true}
            bodyClassName="p-4"
        >
            <Row className="g-4">
                {/* Lateral Esquerda - Detalhes do Plano */}
                <Col lg={7}>
                    <div 
                        className="p-4 h-100" 
                        style={{
                            backgroundColor: "var(--color-bg-canvas)",
                            border: "1.5px solid rgba(72, 39, 18, 0.12)",
                            borderRadius: "16px"
                        }}
                    >
                        <span className="text-xs text-muted d-block fw-bold text-uppercase mb-1" style={{ letterSpacing: "0.5px" }}>
                            Plano Vigente
                        </span>
                        <h4 className="fw-bold m-0" style={{ color: "var(--color-brand-primary)", fontFamily: "Outfit, sans-serif" }}>
                            {planName}
                        </h4>
                        
                        <div className="d-flex align-items-baseline mt-2 mb-3">
                            <span className="fs-3 fw-bold text-dark" style={{ fontFamily: "Outfit, sans-serif" }}>
                                {planPrice}
                            </span>
                            <span className="text-muted text-sm ms-1">
                                /mensal
                            </span>
                        </div>

                        <hr style={{ borderColor: "rgba(72, 39, 18, 0.12)" }} />

                        <div className="mt-3">
                            <span className="text-xs text-muted d-block fw-bold text-uppercase mb-2" style={{ letterSpacing: "0.5px" }}>
                                Recursos e Limites
                            </span>
                            <ul className="list-unstyled d-flex flex-column gap-2.5 m-0 text-sm" style={{ color: "var(--color-brand-secondary)" }}>
                                <li className="d-flex align-items-start gap-2.5">
                                    <i className="ph-light ph-users fs-5 mt-0.5" style={{ color: "var(--color-brand-accent)" }} />
                                    <div style={{ color: "var(--color-brand-primary)" }}>
                                        Limite de Alunos Ativos: <strong style={{ color: "var(--color-brand-primary)" }}>{maxMembers}</strong>
                                    </div>
                                </li>
                                {features.map((feature, i) => (
                                    <li key={i} className="d-flex align-items-start gap-2.5">
                                        <i className="ph-light ph-check-circle fs-5 text-success mt-0.5" />
                                        <span style={{ color: "var(--color-brand-primary)" }}>{feature}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </Col>

                {/* Lateral Direita - Vigência e Operações */}
                <Col lg={5} className="d-flex flex-column justify-content-between">
                    {/* Ciclo Financeiro */}
                    <div 
                        className="p-4 mb-3" 
                        style={{
                            backgroundColor: "var(--color-bg-surface)",
                            border: "1.5px solid rgba(72, 39, 18, 0.12)",
                            borderRadius: "16px"
                        }}
                    >
                        <h6 className="fw-bold text-dark mb-3" style={{ fontFamily: "Outfit, sans-serif", fontSize: "14px" }}>
                            Vigência e Cobrança
                        </h6>
                        
                        <div className="d-flex flex-column gap-3 text-sm">
                            <div>
                                <span className="text-xs text-muted d-block fw-bold text-uppercase" style={{ letterSpacing: "0.5px" }}>
                                    Provedor de Cobrança
                                </span>
                                <span className="fw-bold text-dark">
                                    {tenant.billingProvider === "mercadopago" ? "Mercado Pago" : tenant.billingProvider === "manual" ? "Manual" : tenant.billingProvider || "Manual"}
                                </span>
                            </div>
                            <div>
                                <span className="text-xs text-muted d-block fw-bold text-uppercase" style={{ letterSpacing: "0.5px" }}>
                                    Próximo Vencimento
                                </span>
                                <span className="fw-bold text-dark">
                                    {tenant.dueDate
                                        ? new Date(tenant.dueDate + "T12:00:00").toLocaleDateString("pt-BR")
                                        : "—"}
                                </span>
                            </div>
                            {isTrial && trialRange && (
                                <div>
                                    <span className="text-xs text-muted d-block fw-bold text-uppercase" style={{ letterSpacing: "0.5px" }}>
                                        Período de Teste
                                    </span>
                                    <span className="fw-bold text-dark">
                                        {trialRange}
                                    </span>
                                </div>
                            )}
                            {tenant.createdAt && (
                                <div>
                                    <span className="text-xs text-muted d-block fw-bold text-uppercase" style={{ letterSpacing: "0.5px" }}>
                                        Cliente desde
                                    </span>
                                    <span className="fw-bold text-dark">
                                        {new Date(tenant.createdAt).toLocaleDateString("pt-BR")}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Botões de Ações Comerciais */}
                    <div className="d-flex flex-column gap-2">
                        <NeoButton 
                            variant="primary" 
                            className="py-2.5 w-100 d-flex align-items-center justify-content-center gap-2" 
                            onClick={onAlterPlan}
                        >
                            <i className="ph-light ph-pencil-simple-line fs-5" /> Alterar Plano
                        </NeoButton>

                        <NeoButton
                            variant="soft-primary"
                            className="py-2.5 w-100 d-flex align-items-center justify-content-center gap-2"
                            onClick={onAlterTrial}
                        >
                            <i className="ph-light ph-calendar-plus fs-5" /> Alterar Trial
                        </NeoButton>
                        
                        <NeoButton 
                            variant="soft-danger" 
                            className="py-2.5 w-100 d-flex align-items-center justify-content-center gap-2" 
                            onClick={() => {
                                if (window.confirm("Deseja simular o cancelamento desta assinatura comercial?")) {
                                    setTenant((prev) => ({ ...prev, status: "cancelled" }));
                                    handleLogAction("subscription_cancelled", "Cancelou assinatura do cliente (simulado)");
                                }
                            }}
                        >
                            <i className="ph-light ph-trash fs-5" /> Cancelar Contrato
                        </NeoButton>
                    </div>
                </Col>
            </Row>
        </NeoCard>
    );
};

DetailSubscription.propTypes = {
    tenant: PropTypes.object.isRequired,
    setTenant: PropTypes.func.isRequired,
    handleLogAction: PropTypes.func.isRequired,
    onAlterPlan: PropTypes.func.isRequired,
    onAlterTrial: PropTypes.func.isRequired
};

export default DetailSubscription;
